import React, { useState, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
  HighlightAlt as HighlightAltIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StudyNotesContent } from '../../services/contentService';

interface StudyNotesProps {
  content: StudyNotesContent;
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

// Interface for table content used in the component
interface TableContent {
  title: string;
  content: string;
}

const StudyNotes: React.FC<StudyNotesProps> = ({
  content,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState(0);
  
  // State for search query
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for highlighted text
  const [highlights, setHighlights] = useState<string[]>([]);
  
  // Ref for content container
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Default content if properties are missing
  const fullNotes = content.fullNotes || content.sections?.map(section => 
    `## ${section.heading}\n\n${section.content}`
  ).join('\n\n') || '';
  
  const summary = content.summary || content.sections?.find(s => 
    s.heading.toLowerCase().includes('summary')
  )?.content || '';
  
  // Ensure keyPoints is either a string array or a string
  const keyPoints: string[] | string = content.keyPoints || 
    (content.sections?.find(s => 
      s.heading.toLowerCase().includes('key') || 
      s.heading.toLowerCase().includes('point')
    )?.content.split('\n').filter(line => line.trim().length > 0)) || 
    '';
  
  // Convert tables from the new format to the old format
  const getTables = (): TableContent[] => {
    if (content.tables) {
      return content.tables.map(table => ({
        title: table.title,
        content: `| ${table.headers.join(' | ')} |\n| ${table.headers.map(() => '---').join(' | ')} |\n${
          table.rows.map(row => `| ${row.join(' | ')} |`).join('\n')
        }`
      }));
    }
    
    // Try to extract tables from sections
    const tableSections = content.sections?.filter(s => 
      s.heading.toLowerCase().includes('table')
    ) || [];
    
    if (tableSections.length > 0) {
      return tableSections.map(section => ({
        title: section.heading,
        content: section.content
      }));
    }
    
    return [];
  };
  
  const tables = getTables();
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle copy
  const handleCopy = () => {
    let textToCopy = '';
    
    switch (activeTab) {
      case 0: // Full Notes
        textToCopy = fullNotes;
        break;
      case 1: // Summary
        textToCopy = summary;
        break;
      case 2: // Key Points
        textToCopy = Array.isArray(keyPoints) 
          ? keyPoints.map((point: string) => `• ${point}`).join('\n\n')
          : keyPoints;
        break;
      case 3: // Tables
        textToCopy = tables.map((table: TableContent) => 
          `# ${table.title}\n\n${table.content}`
        ).join('\n\n');
        break;
      default:
        textToCopy = fullNotes;
    }
    
    navigator.clipboard.writeText(textToCopy);
    onCopy();
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMoreMenuClose();
  };
  
  // Handle download
  const handleDownload = () => {
    let textToDownload = '';
    let filenameSuffix = '';
    
    switch (activeTab) {
      case 0: // Full Notes
        textToDownload = fullNotes;
        filenameSuffix = 'full_notes';
        break;
      case 1: // Summary
        textToDownload = summary;
        filenameSuffix = 'summary';
        break;
      case 2: // Key Points
        textToDownload = Array.isArray(keyPoints) 
          ? keyPoints.map((point: string) => `• ${point}`).join('\n\n')
          : keyPoints;
        filenameSuffix = 'key_points';
        break;
      case 3: // Tables
        textToDownload = tables.map((table: TableContent) => 
          `# ${table.title}\n\n${table.content}`
        ).join('\n\n');
        filenameSuffix = 'tables';
        break;
      default:
        textToDownload = fullNotes;
        filenameSuffix = 'full_notes';
    }
    
    // Create a blob with the content
    const blob = new Blob([textToDownload], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and click it
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${filenameSuffix}.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onDownload();
    handleMoreMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    let contentHtml = '';
    let titleSuffix = '';
    
    switch (activeTab) {
      case 0: // Full Notes
        contentHtml = `<div class="content">${fullNotes.replace(/\n/g, '<br>')}</div>`;
        titleSuffix = 'Full Notes';
        break;
      case 1: // Summary
        contentHtml = `<div class="content">${summary.replace(/\n/g, '<br>')}</div>`;
        titleSuffix = 'Summary';
        break;
      case 2: // Key Points
        contentHtml = Array.isArray(keyPoints)
          ? `<ul>${keyPoints.map((point: string) => `<li>${point}</li>`).join('')}</ul>`
          : `<div class="content">${typeof keyPoints === 'string' ? keyPoints.replace(/\n/g, '<br>') : ''}</div>`;
        titleSuffix = 'Key Points';
        break;
      case 3: // Tables
        contentHtml = tables.map((table: TableContent) => 
          `<div class="table-section">
            <h2>${table.title}</h2>
            <div class="table-content">${table.content.replace(/\n/g, '<br>')}</div>
          </div>`
        ).join('');
        titleSuffix = 'Tables';
        break;
      default:
        contentHtml = `<div class="content">${fullNotes.replace(/\n/g, '<br>')}</div>`;
        titleSuffix = 'Full Notes';
    }
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${videoTitle} - ${titleSuffix}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 20px;
                color: #333;
              }
              h1 {
                font-size: 24px;
                margin-bottom: 20px;
              }
              .time-saved {
                color: #06d6a0;
                font-weight: bold;
                margin-bottom: 20px;
              }
              h2 {
                font-size: 20px;
                margin-top: 30px;
                margin-bottom: 15px;
                color: #00b4d8;
              }
              .content {
                margin-bottom: 20px;
              }
              ul {
                padding-left: 20px;
              }
              li {
                margin-bottom: 10px;
              }
              .table-section {
                margin-bottom: 30px;
                page-break-inside: avoid;
              }
              .table-content {
                margin-left: 15px;
              }
              @media print {
                body {
                  font-size: 12pt;
                }
                h1 {
                  font-size: 18pt;
                }
                h2 {
                  font-size: 16pt;
                }
              }
            </style>
          </head>
          <body>
            <h1>${videoTitle} - ${titleSuffix}</h1>
            <div class="time-saved">Saved ${timeSaved} minutes</div>
            ${contentHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    
    onPrint();
    handleMoreMenuClose();
  };
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchQuery('');
  };
  
  // Handle search change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle highlight text
  const handleHighlightText = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim() !== '') {
      const selectedText = selection.toString().trim();
      if (!highlights.includes(selectedText)) {
        setHighlights([...highlights, selectedText]);
      }
    }
  };
  
  // Get highlighted content
  const getHighlightedContent = (text: string): React.ReactNode => {
    if (!searchQuery && highlights.length === 0) {
      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {text}
        </ReactMarkdown>
      );
    }
    
    let processedText = text;
    
    // Apply search highlighting
    if (searchQuery) {
      const regex = new RegExp(`(${searchQuery})`, 'gi');
      processedText = processedText.replace(regex, '**$1**');
    }
    
    // Apply user highlights
    highlights.forEach(highlight => {
      if (highlight) {
        const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        processedText = processedText.replace(regex, '===$1===');
      }
    });
    
    return (
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          strong: ({ node, ...props }) => (
            <mark style={{ backgroundColor: 'rgba(255, 213, 0, 0.4)', fontWeight: 'normal' }} {...props} />
          ),
          p: ({ node, ...props }) => {
            const content = props.children?.toString() || '';
            if (content.includes('===')) {
              // Custom rendering for highlighted text
              const parts = content.split(/(===.*?===)/g);
              return (
                <p>
                  {parts.map((part, i) => {
                    if (part.startsWith('===') && part.endsWith('===')) {
                      return (
                        <mark 
                          key={i} 
                          style={{ backgroundColor: 'rgba(0, 180, 216, 0.2)' }}
                        >
                          {part.slice(3, -3)}
                        </mark>
                      );
                    }
                    return part;
                  })}
                </p>
              );
            }
            return <p {...props} />;
          }
        }}
      >
        {processedText}
      </ReactMarkdown>
    );
  };
  
  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Full Notes
        return (
          <Box 
            ref={contentRef}
            sx={{ 
              typography: 'body1',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 3,
                mb: 2,
                fontWeight: 600,
              },
              '& p': {
                mb: 2,
              },
              '& ul, & ol': {
                mb: 2,
                pl: 4,
              },
              '& li': {
                mb: 1,
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                py: 1,
                my: 2,
                bgcolor: 'rgba(0, 180, 216, 0.05)',
              },
              '& code': {
                fontFamily: 'monospace',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                p: 0.5,
                borderRadius: 1,
              },
              '& pre': {
                fontFamily: 'monospace',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                p: 2,
                borderRadius: 1,
                overflowX: 'auto',
                mb: 2,
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
              },
              '& th': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                fontWeight: 600,
              },
            }}
          >
            {getHighlightedContent(fullNotes)}
          </Box>
        );
      case 1: // Summary
        return (
          <Box 
            ref={contentRef}
            sx={{ 
              typography: 'body1',
              '& p': {
                mb: 2,
              },
            }}
          >
            {getHighlightedContent(summary)}
          </Box>
        );
      case 2: // Key Points
        return (
          <Box ref={contentRef}>
            {Array.isArray(keyPoints) ? (
              <ul style={{ paddingLeft: '20px' }}>
                {keyPoints.map((point: string, index: number) => (
                  <li key={index} style={{ marginBottom: '12px' }}>
                    {getHighlightedContent(point)}
                  </li>
                ))}
              </ul>
            ) : (
              getHighlightedContent(keyPoints)
            )}
          </Box>
        );
      case 3: // Tables
        return (
          <Box ref={contentRef}>
            {tables.map((table: TableContent, index: number) => (
              <Box key={index} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {table.title}
                </Typography>
                <Box 
                  sx={{ 
                    typography: 'body1',
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      mb: 2,
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1,
                    },
                    '& th': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      fontWeight: 600,
                    },
                  }}
                >
                  {getHighlightedContent(table.content)}
                </Box>
              </Box>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };
  
  // Check if we should show the Tables tab
  const showTablesTab = tables.length > 0;
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Study Notes
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Highlight selected text">
              <IconButton onClick={handleHighlightText}>
                <HighlightAltIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search in notes..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            },
          }}
        />
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="study notes tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<MenuBookIcon />} 
            label="Full Notes" 
            id="notes-tab-0"
            aria-controls="notes-tabpanel-0" 
          />
          <Tab 
            icon={<FormatListBulletedIcon />} 
            label="Summary" 
            id="notes-tab-1"
            aria-controls="notes-tabpanel-1" 
          />
          <Tab 
            icon={<FormatListNumberedIcon />} 
            label="Key Points" 
            id="notes-tab-2"
            aria-controls="notes-tabpanel-2" 
          />
          {showTablesTab && (
            <Tab 
              icon={<TableChartIcon />} 
              label="Tables" 
              id="notes-tab-3"
              aria-controls="notes-tabpanel-3" 
            />
          )}
        </Tabs>
      </Box>
      
      {/* Tab content */}
      <Box 
        role="tabpanel"
        id={`notes-tabpanel-${activeTab}`}
        aria-labelledby={`notes-tab-${activeTab}`}
      >
        {renderTabContent()}
      </Box>
      
      {/* Highlighted text list */}
      {highlights.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Your Highlights
          </Typography>
          <Box sx={{ bgcolor: 'rgba(0, 180, 216, 0.05)', p: 2, borderRadius: 2 }}>
            {highlights.map((highlight, index) => (
              <Box 
                key={index} 
                sx={{ 
                  mb: 1, 
                  pb: 1, 
                  borderBottom: index < highlights.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                }}
              >
                <Typography variant="body2">
                  "{highlight}"
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => setHighlights([])}
                sx={{ borderRadius: 4 }}
              >
                Clear All
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default StudyNotes;
